import { useCallback, useMemo } from 'react'
import { useHistory, useLocation } from 'react-router'
import type { History } from 'history'
import urlJoin from 'url-join'

import { MenuGroup } from './types'

// Parse filters from current URL (following app pattern)
export const useFilterParams = (): Set<string> => {
  const { search } = useLocation()

  return useMemo(() => {
    const params = new URLSearchParams(search)
    const filtersParam = params.get('filters')
    return filtersParam ? new Set(filtersParam.split(',')) : new Set()
  }, [search])
}

// URL update hook (following app pattern with useHistory)
export const useUpdateFiltersURL = () => {
  const history = useHistory()
  const { search, pathname } = useLocation()

  return useCallback(
    (filters: Set<string>, replace = false) => {
      const params = new URLSearchParams(search)

      if (filters.size > 0) {
        params.set('filters', Array.from(filters).join(','))
      } else {
        params.delete('filters')
      }

      const newUrl = `${pathname}?${params.toString()}`

      if (replace) {
        history.replace(newUrl)
      } else {
        history.push(newUrl)
      }
    },
    [history, search, pathname]
  )
}

// Helper to validate if a filter exists in menu structure
export const isValidFilter = (
  filter: string,
  menuGroups: MenuGroup[]
): boolean => {
  const parts = filter.split(':')

  if (parts.length === 1) {
    // Top-level group
    return menuGroups.some((g) => g.guid === parts[0])
  } else if (parts.length === 2) {
    // Parent:child
    const parentGroup = menuGroups.find((g) => g.guid === parts[0])
    return parentGroup?.children?.some((c) => c.guid === parts[1]) || false
  } else if (parts.length === 3) {
    // Parent:child:grandchild
    const parentGroup = menuGroups.find((g) => g.guid === parts[0])
    const childGroup = parentGroup?.children?.find((c) => c.guid === parts[1])
    return childGroup?.children?.some((gc) => gc.guid === parts[2]) || false
  }

  return false
}
const buildCategoryUrl = (
  group: MenuGroup,
  ecommPath: string | null | undefined
) => {
  // Build category URL using slug
  const categorySlug = group.slug
  return urlJoin(ecommPath ?? '', `/categories/${categorySlug}`)
}

export const navigateToCategory = (
  group: MenuGroup,
  ecommPath: string | null | undefined,
  history: History,
  onClose: () => void
) => {
  const url = buildCategoryUrl(group, ecommPath)
  history.push(url)
  onClose()
}
